/* eslint-disable no-underscore-dangle */
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, OnInit } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { BroadcastService } from '@services/broadcast-service';
import { AppConfig } from 'client/app/app.config';
import SwiperCore, { Scrollbar, Autoplay, SwiperOptions } from 'swiper';
import {AppWebBridgeService} from '@services/app-web-bridge-service';

SwiperCore.use([Autoplay, Scrollbar]);

@Component({
  selector: 'feed',
  templateUrl: './feed.html',
  styleUrls: ['../../user-explore.scss'],
  standalone: false,
})
export class FeedComponent implements OnChanges, OnInit {
  private _feed: any;
  private readonly viewedSlides: Set<number> = new Set();
  private swiperInstance: any;
  private currentFeedId: string;

  @Input() set feed(feed: any) {
    if (feed && feed?.media) {
      this._feed = feed;
    } else if (feed && !feed?.media) {
      this._feed = {
        ...feed,
        media: [{
          url: feed?.url,
          aspectRatio: feed?.aspectRatio,
          type: feed?.type,
          thumbnailUrl: feed?.thumbnailUrl,
        }],
      };
    }
  }

  get feed(): any { return this._feed; }

  get isSingleImage(): boolean {
    return this.feed?.media?.length === 1 && this.feed?.media[0]?.type === this.appConfig.Shared.Feed.IMAGE;
  }

  @Input() index: number;
  @Input() visible: boolean;
  @Input() isLiked: boolean;
  @Output() onVideoPlay: EventEmitter<any> = new EventEmitter();
  @Output() onCTAClick: EventEmitter<any> = new EventEmitter();
  @Output() likeToggle: EventEmitter<any> = new EventEmitter();

  carouselConfig: SwiperOptions = {
    slidesPerView: 1,
    navigation: { prevEl: 'nonext', nextEl: 'nonext' },
    pagination: true,
    loop: false,
    on: {
      slideChangeTransitionEnd: () => {
        if (this.swiperInstance) {
          this.onSlideChangeEnd(this.swiperInstance);
        }
      }
    }
  };
  playLikeAnimation: boolean = false;
  timeoutInterval: any;
  user: any;
  isPaidUser: boolean = false;
  isCtaDisabled: boolean = false;
  branchLink: string;
  isNotIOSDevice: boolean = true;

  constructor(
    private eventLogger: EventLoggerService,
    public appConfig: AppConfig,
    public conn: ConnectionService,
    private readonly broadcast: BroadcastService,
    private readonly appBridge: AppWebBridgeService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset viewed slides when feed changes
    if (changes.feed && this.feed?.objectId !== this.currentFeedId) {
      this.currentFeedId = this.feed?.objectId;
      this.viewedSlides.clear();
      if (this.swiperInstance) {
        // Add current slide to viewed slides after feed change
        this.viewedSlides.add(this.swiperInstance.activeIndex);
      }
    }
  }

  async ngOnInit(): Promise<void> {
    this.user = await this.conn.getActingUser();
    if (this.user.get('orderState') === this.appConfig.Shared.User.OrderState.DELIVERED) this.isPaidUser = true;
    if (!this.isPaidUser && this.feed.ctaText === 'Buy') {
      this.isCtaDisabled = true;
    }
    this.currentFeedId = this.feed?.objectId;
    this.isNotIOSDevice = (await this.appBridge?.requestOSInformation()) !== 'iOS';
  }

  onVideoPlayWithTracking(index: number): void {
    this.onVideoPlay.emit(index);
    this.eventLogger.cleverTapEvent('click', JSON.stringify({
      name: 'explore-feed-video-played',
      id: this.feed.objectId
    }));
  }

  async updateLikes(isLiked: boolean): Promise<void> {
    this.isLiked = isLiked;
    this.likeToggle.emit(this.isLiked);
  }

  updateIsLikedFromFeedImageComponent(isLiked:boolean): void {
    this.isLiked = isLiked;
  }

  onLikeClick(): void {
    if (this.isLiked) return;
    this.isLiked = !this.isLiked;
    if (this.isLiked) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'explore-feed-liked', id: this.feed.objectId }));
      this.playHeartAnimation();
    }
    this.likeToggle.emit(this.isLiked);
  }

  playHeartAnimation(): void {
    clearTimeout(this.timeoutInterval);
    this.playLikeAnimation = true;
    this.timeoutInterval = setTimeout((): boolean => this.playLikeAnimation = false, 500);
  }

  onSwiperInit(swiper: any): void {
    this.swiperInstance = swiper;
    // Add initial slide to viewed slides without sending event
    this.viewedSlides.add(swiper.activeIndex);
  }

  onSingleImageClick(): void {
    if (this.isSingleImage) {
      this.eventLogger.cleverTapEvent('click', JSON.stringify({
        name: 'explore_feed_image_click',
        id: this.feed.objectId
      }));
    }
  }

  private sendSlideViewEvent(): void {
    if (this.viewedSlides.size > 1) { // Only send event if more than one slide has been viewed
      this.eventLogger.cleverTapEvent('click', JSON.stringify({
        id: this.feed.objectId,
        type: 'feed',
        subtype: 'carousel',
        slides_viewed: this.viewedSlides.size,
        total_slides: this.feed.media.length,
        name: 'explore_slide_view',
      }));
    }
  }

  private onSlideChangeEnd(swiper: any): void {
    if (!swiper) return;

    const currentIndex = swiper.activeIndex;
    const previousSize = this.viewedSlides.size;
    this.viewedSlides.add(currentIndex);

    // Only send event if we've actually viewed a new slide
    if (this.viewedSlides.size > previousSize) {
      this.sendSlideViewEvent();
    }
  }

  async onShareClick(): Promise<void> {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({
      name: 'explore-feed-share-clicked',
      id: this.feed.objectId,
      type: this.feed.media?.[0]?.type || 'UNKNOWN'
    }));
    await this.getBranchLink();

    const shareText = this.branchLink;

    if (this.branchLink?.length > 0) {
      this.broadcast.broadcast('SHARE', {
        type: 'EXPLORE_FEED',
        text: shareText,
      });
    }
  }

  async getBranchLink(): Promise<void> {
    const response = await this.conn.getBranchLink(this.feed?.objectId);
    this.branchLink = response?.data?.branchLink || '';
  }
}
